import { createClient } from '@supabase/supabase-js';

// ---------------------------------------------------------------------------
// Active Supabase project
// ---------------------------------------------------------------------------
// The previous project (vsxovudqkzjplcdobnbh) was deleted, which caused
// "Project has been deleted" errors for any client that still had a cached
// session keyed to that project ref. We now point exclusively at the active
// project below. Do NOT add any other Supabase URLs here.
const supabaseUrl = 'https://vadsiqsuomscnuiuyreo.supabase.co';
const supabaseKey =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZhZHNpcXN1b21zY251aXV5cmVvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgxMDQzNTEsImV4cCI6MjA5MzY4MDM1MX0.6GAL26u3kRl4C94_Xcex-feHYQZ9L4yf46r6pUIS1k8';

const ACTIVE_PROJECT_REF = 'vadsiqsuomscnuiuyreo';
const STALE_PROJECT_REFS = ['vsxovudqkzjplcdobnbh'];

// ---------------------------------------------------------------------------
// One-time cleanup of any cached auth/session state that belonged to a
// previous (now-deleted) Supabase project. supabase-js stores the session
// under a key shaped `sb-<projectRef>-auth-token` (plus a couple of helper
// keys). If we don't strip those out, the auth listener will try to refresh
// a token issued by a deleted project and surface "Project has been deleted"
// to the user before they ever see a login form.
// ---------------------------------------------------------------------------
function purgeStaleSupabaseStorage() {
  if (typeof window === 'undefined') return;
  const stamp = `kc:supabase-active:${ACTIVE_PROJECT_REF}`;

  const purgeFrom = (store: Storage) => {
    const toRemove: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (!key) continue;
      // Drop anything that looks like a Supabase auth blob from a stale project,
      // and also drop legacy `supabase.auth.token` entries.
      const isStaleProjectKey = STALE_PROJECT_REFS.some(
        (ref) => key.includes(ref) || key === `sb-${ref}-auth-token`,
      );
      const isLegacySupabaseKey = key === 'supabase.auth.token';
      if (isStaleProjectKey || isLegacySupabaseKey) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => {
      try {
        store.removeItem(k);
      } catch {
        /* ignore */
      }
    });
  };

  try {
    // Only run the purge once per browser, otherwise we'd nuke the active
    // session every page load. The marker itself is keyed to the active
    // project ref so swapping projects later re-triggers the cleanup.
    if (localStorage.getItem(stamp) === '1') return;
    purgeFrom(localStorage);
    try {
      purgeFrom(sessionStorage);
    } catch {
      /* sessionStorage may be unavailable in some embeds */
    }
    localStorage.setItem(stamp, '1');
  } catch {
    /* storage may be blocked (private mode, etc.) — safe to ignore */
  }
}

purgeStaleSupabaseStorage();

const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    storageKey: `sb-${ACTIVE_PROJECT_REF}-auth-token`,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

export { supabase };
